// Lightweight justified grid layout (inspired by Squarespace gallery-strips)
// Usage: <ul class="js-justified" data-row-height="280" data-gutter="10"> ... <li><img ... /></li> ...</ul>

function computeAspectRatio(img) {
  const w = Number(
    img.getAttribute("data-w") || img.naturalWidth || img.width || 1
  );
  const h = Number(
    img.getAttribute("data-h") || img.naturalHeight || img.height || 1
  );
  return Math.max(1, w) / Math.max(1, h);
}

function layoutJustified(list) {
  if (!list) return;
  // Masquer lors du premier calcul pour éviter le FOUC
  if (!list.__layoutReady) {
    list.style.visibility = "hidden";
  }
  const gutter = Number(list.dataset.gutter || 8);
  const target = Number(
    list.dataset.rowHeight || list.dataset.rowheight || 280
  );
  const justifyLast = String(list.dataset.justifyLast || "").toLowerCase();
  const shouldJustifyLast =
    justifyLast === "1" || justifyLast === "true" || justifyLast === "yes";
  const items = Array.from(list.querySelectorAll("li"));
  if (items.length === 0) return;
  const cells = items.map((li, index) => {
    const img = li.querySelector("img");
    return { index, li, img, ratio: computeAspectRatio(img) };
  });

  // prepare absolute container
  list.style.position = "relative";
  items.forEach((li) => {
    li.style.position = "absolute";
    li.style.top = "0px";
    li.style.left = "0px";
  });

  // reset height to let clientWidth settle correctly in some browsers
  list.style.height = "auto";
  const width = Math.round(list.getBoundingClientRect().width);
  if (!width) return;

  let y = 0;
  let row = [];
  let sumRatios = 0;
  let prevRowHeight = target; // mémorise la hauteur de la rangée précédente

  function flushRow(justify = true, isLastRow = false) {
    if (row.length === 0) return 0;
    const gaps = gutter * (row.length - 1);
    const ideal = (width - gaps) / Math.max(0.0001, sumRatios);
    const height = justify
      ? Math.max(1, Math.round(ideal))
      : Math.max(1, Math.round(Math.min(target, ideal)));
    let x = 0;
    // Cas spécial: dernière ligne orpheline d'un seul item sur desktop → réduire et centrer
    const isDesktop = width >= 768; // seuil ajustable
    if (isLastRow && row.length === 1 && isDesktop) {
      const single = row[0];
      // Reprendre la hauteur de la rangée précédente (fallback: target)
      const orphanHeight = Math.max(1, Math.round(prevRowHeight || target));
      let orphanWidth = Math.round(orphanHeight * single.ratio);
      const maxWidth = Math.round(width * 0.66); // ne pas dépasser ~66% du conteneur
      if (orphanWidth > maxWidth) orphanWidth = maxWidth;
      // Aligner à gauche
      single.li.style.transform = `translate(0px, ${y}px)`;
      single.li.style.width = `${orphanWidth}px`;
      single.img.style.height = `${orphanHeight}px`;
      single.img.style.width = "100%";
      return orphanHeight;
    }

    for (let i = 0; i < row.length; i += 1) {
      const cell = row[i];
      let w = Math.round(height * cell.ratio);
      if (i === row.length - 1) {
        // Ensure last item never exceeds container
        const remaining = width - x;
        if (w > remaining) w = remaining;
      }
      cell.li.style.transform = `translate(${x}px, ${y}px)`;
      cell.li.style.width = `${w}px`;
      cell.img.style.height = `${height}px`;
      cell.img.style.width = "100%";
      x += w + (i < row.length - 1 ? gutter : 0);
    }
    return height;
  }

  for (let i = 0; i < cells.length; i += 1) {
    const cell = cells[i];
    // Test en flottant: largeur estimée de la ligne à hauteur cible, avec gouttières
    const projected = (sumRatios + cell.ratio) * target + gutter * row.length;
    if (projected > width && row.length > 0) {
      const h = flushRow(true);
      y += h + gutter;
      row = [];
      sumRatios = 0;
      prevRowHeight = h; // mémorise la hauteur utilisée pour la ligne flushée
    }
    row.push(cell);
    sumRatios += cell.ratio;
  }

  // last row not justified
  if (row.length > 0) {
    const h = flushRow(shouldJustifyLast, true);
    y += h;
  }

  list.style.height = `${y}px`;

  // Révéler une fois le premier layout terminé
  if (!list.__layoutReady) {
    list.style.visibility = "";
    list.__layoutReady = true;
  }
}

export function initJustified() {
  const lists = Array.from(document.querySelectorAll(".js-justified"));
  const relayout = () => lists.forEach(layoutJustified);
  relayout();

  // Re-layout on images load to avoid ratio 0 cases
  lists.forEach((list) => {
    const imgs = Array.from(list.querySelectorAll("img"));
    imgs.forEach((img) => {
      if (img.complete) return;
      img.addEventListener("load", () => layoutJustified(list), { once: true });
    });
    // ResizeObserver for container width changes
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => layoutJustified(list));
      ro.observe(list);
    }
  });

  window.addEventListener("resize", () => {
    // debounce simple
    clearTimeout(window.__justifiedTimer);
    window.__justifiedTimer = setTimeout(relayout, 150);
  });
}

document.addEventListener("DOMContentLoaded", initJustified);
